import axios, { AxiosInstance } from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';

// Use production API URL in production, local in development
const API_URL = window.location.protocol === 'https:'
  ? (process.env.REACT_APP_PRODUCTION_API_URL || 'https://saeg.japaneast.cloudapp.azure.com/ea-api') + '/api'
  : (process.env.REACT_APP_API_URL || `http://${window.location.hostname}:3010/api`);

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token
    this.client.interceptors.request.use(
      async (config) => {
        try {
          const session = await fetchAuthSession();
          const token = session.tokens?.idToken?.toString();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch (error) {
          console.error('Error fetching auth session:', error);
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  // EA Notebook endpoints
  async getNotebooks(): Promise<any[]> {
    try {
      const response = await this.client.get('/ea-notebooks');
      const notebooks = response.data?.notebooks || response.data;
      return Array.isArray(notebooks) ? notebooks : [];
    } catch (err) {
      console.error('Failed to get notebooks:', err);
      return [];
    }
  }

  async getNotebook(notebookId: string): Promise<any> {
    const response = await this.client.get(`/ea-notebooks/${notebookId}`);
    return response.data;
  }

  async createNotebook(data: { title: string; description?: string }): Promise<any> {
    const response = await this.client.post('/ea-notebooks', data);
    return response.data;
  }

  async updateNotebook(notebookId: string, data: { title?: string; description?: string }): Promise<any> {
    const response = await this.client.put(`/ea-notebooks/${notebookId}`, data);
    return response.data;
  }

  async deleteNotebook(notebookId: string): Promise<void> {
    await this.client.delete(`/ea-notebooks/${notebookId}`);
  }

  async getNotebookSources(notebookId: string): Promise<any[]> {
    try {
      const response = await this.client.get(`/ea-notebooks/${notebookId}/sources`);
      const sources = response.data?.sources || response.data;
      return Array.isArray(sources) ? sources : [];
    } catch (err) {
      console.error('Failed to get sources:', err);
      return [];
    }
  }

  async uploadNotebookSource(notebookId: string, file: File, name?: string): Promise<any> {
    const formData = new FormData();
    if (name) formData.append('name', name);   // must precede the file part for multer
    formData.append('file', file);
    const response = await this.client.post(`/ea-notebooks/${notebookId}/sources/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
    return response.data;
  }

  async addNotebookUrl(notebookId: string, url: string): Promise<any> {
    const response = await this.client.post(`/ea-notebooks/${notebookId}/sources/url`, { url });
    return response.data;
  }

  async addNotebookText(notebookId: string, text: string, name?: string): Promise<any> {
    const response = await this.client.post(`/ea-notebooks/${notebookId}/sources/text`, { text, name });
    return response.data;
  }

  async deleteNotebookSource(notebookId: string, sourceId: string): Promise<void> {
    await this.client.delete(`/ea-notebooks/${notebookId}/sources/${sourceId}`);
  }

  async analyzeNotebookSource(notebookId: string, sourceId: string): Promise<any> {
    const response = await this.client.post(`/ea-notebooks/${notebookId}/sources/${sourceId}/analyze`, {}, { timeout: 300000 });
    return response.data;
  }

  async analyzeNotebookSources(notebookId: string): Promise<any> {
    const response = await this.client.post(`/ea-notebooks/${notebookId}/analyze`, {}, { timeout: 600000 });
    return response.data;
  }

  async chatWithNotebook(notebookId: string, message: string): Promise<{ answer: string }> {
    const response = await this.client.post(`/ea-notebooks/${notebookId}/chat`, { message }, { timeout: 180000 });
    return response.data;
  }

  async getNotebookChat(notebookId: string): Promise<any[]> {
    try {
      const response = await this.client.get(`/ea-notebooks/${notebookId}/chat`);
      return Array.isArray(response.data?.messages) ? response.data.messages : [];
    } catch (err) {
      console.error('Failed to get chat history:', err);
      return [];
    }
  }

  async clearNotebookChat(notebookId: string): Promise<void> {
    await this.client.delete(`/ea-notebooks/${notebookId}/chat`);
  }

  // Notes
  async getNotebookNotes(notebookId: string): Promise<any[]> {
    try {
      const response = await this.client.get(`/ea-notebooks/${notebookId}/notes`);
      return Array.isArray(response.data?.notes) ? response.data.notes : [];
    } catch (err) {
      console.error('Failed to get notes:', err);
      return [];
    }
  }

  async createNotebookNote(notebookId: string, data: { title?: string; content: string }): Promise<any> {
    const response = await this.client.post(`/ea-notebooks/${notebookId}/notes`, data);
    return response.data;
  }

  async updateNotebookNote(notebookId: string, noteId: string, data: { title?: string; content?: string }): Promise<any> {
    const response = await this.client.put(`/ea-notebooks/${notebookId}/notes/${noteId}`, data);
    return response.data;
  }

  async deleteNotebookNote(notebookId: string, noteId: string): Promise<void> {
    await this.client.delete(`/ea-notebooks/${notebookId}/notes/${noteId}`);
  }

  // LLM Configuration endpoints
  async getLLMConfigurations(): Promise<any[]> {
    const response = await this.client.get('/llm/configurations');
    return response.data?.configurations || [];
  }

  async getActiveLLMConfiguration(): Promise<any | null> {
    const response = await this.client.get('/llm/active');
    return response.data || null;
  }

  async testLLM(message: string): Promise<{ success: boolean; response: string }> {
    const response = await this.client.post('/llm/test', { message });
    return response.data;
  }
}

export const apiClient = new ApiClient();
export default apiClient;
